/**
 * 地图能力 Service —— 业务组件与地图厂商之间唯一的接触面
 *
 * 能力三件套（相互独立，任一失败不影响其余）：
 *   getMapConfig()    后端 /map/config → { enabled, provider, key?/jsKey? }
 *                     （无 Key/网络失败/未知 provider 一律折叠为 enabled:false，永不抛）
 *   resolveAddress()  地址→规范坐标对象 { latitude, longitude, coordinateSystem, matchedLevel, matchedName }
 *                     （逐级降级在后端阶梯完成：全址→去门牌→区→市→省；失败 60s 短缓存后返回 null）
 *   createMap()       el + 坐标 → provider 渲染 → { destroy() }
 *                     （无 provider/坐标系不支持/SDK 失败 → reject，由组件降级为文本+导航）
 *
 * 厂商隔离：T 与 AMap 的全局对象只存在于 providers/；后端 config 下发 provider 名，
 * 本文件按注册表分发。接新厂商 = 新增 provider 文件 + 注册 + 后端下发其名。
 *
 * Provider 现状（2026-08-28 任务7 R2）：
 *   tianditu —— 正式 Provider（订单地图展示）；坐标系边界转换在 provider 内完成
 *   amap     —— 迁移兼容（仅后端显式 MAP_PROVIDER=amap 时才可能选中，禁止静默回退）
 *
 * 坐标系：全库历史坐标 GCJ-02（订单快照 + 高德 geocode 产出），legacy 默认值声明在此，
 * 不做 schema 迁移。GPS（WGS84）来源经本文件 wgs84ToGcj02 入口归一，业务组件不碰数学。
 */
import axios from 'axios'
import amapProvider from './providers/amap.js'
import tiandituProvider from './providers/tianditu.js'
import { wgs84ToGcj02 } from './coordTransform.js'

// provider 注册表：后端 /map/config 的 provider 名 → 实现
const PROVIDERS = { tianditu: tiandituProvider, amap: amapProvider }

// legacy 默认坐标系（与后端 geocode 响应的 coordinateSystem 字段一致）
export const LEGACY_COORDINATE_SYSTEM = 'GCJ02'

/** GPS 原始坐标（WGS84）→ 库内规范 GCJ-02。业务组件只准用这个入口，不准自己算。 */
export function gpsToCanonical (lat, lng) {
  return wgs84ToGcj02(Number(lat), Number(lng))
}

// ---------- config：页面生命周期内取一次，失败折叠为 enabled:false ----------
let configPromise = null

export function getMapConfig (force = false) {
  if (!force && configPromise) return configPromise
  configPromise = axios.get('/map/config', { timeout: 8000 })
    .then((res) => {
      const d = (res && res.data && res.data.data) || {}
      return (d.enabled && d.provider && PROVIDERS[d.provider]) ? d : { enabled: false, provider: null }
    })
    .catch(() => ({ enabled: false, provider: null }))
  return configPromise
}

// ---------- geocode：地址→坐标（供地图渲染的"数据增强"，与导航按钮无关） ----------
async function geocodeOnce (address) {
  const addr = String(address || '').trim()
  if (!addr) return null
  try {
    const res = await axios.get('/map/geocode', { params: { address: addr }, timeout: 8000 })
    const d = (res && res.data && res.data.data) || {}
    if (d.latitude != null && d.longitude != null) {
      return {
        latitude: Number(d.latitude),
        longitude: Number(d.longitude),
        coordinateSystem: d.coordinateSystem || LEGACY_COORDINATE_SYSTEM,
        // 精度标注（2026-09-19 逐级降级，后端 geocodeService 阶梯产出）：
        // exact/road/district/city/province + 提示名（如「南山区」）——只用于展示提示
        // 与导航坐标门槛；exact 无名。旧后端无此字段 → null（调用方按 exact 兼容）
        matchedLevel: d.matched_level || null,
        matchedName: d.matched_name || null
      }
    }
    return null // 失败响应形如 { success:false }，缺坐标字段即失败
  } catch (e) {
    return null
  }
}

// 地址 → 解析结果进程内记忆：地址是不可变的订单快照，同一地址会话内坐标恒定。
// 不记忆的话，每次开订单抽屉都重走网络 geocode——一次网络抖动 → 塌缩（§13），
// 地图卡 202px→32px 只剩导航行（实测复现），表现为"关了抽屉再开地图忽有忽无"。
// 成功：会话内恒定；失败：60s 短缓存（2026-09-19——失败也不许每次开订单都打网络，
// 短 TTL 兼顾瞬时故障后的自然重试，不把瞬时失败钉死）。
const geoMemo = new Map()
const GEO_FAIL_TTL = 60 * 1000

/**
 * 地址 → 坐标 + 精度：逐级降级在后端 geocodeService 阶梯内完成（全址→去门牌→区→市→省），
 * 本函数单发一次请求。返回 null 即五级全灭 → 调用方走"地址文本+导航"塌缩，不区分失败原因。
 * @returns {Promise<{latitude:number, longitude:number, coordinateSystem:string, matchedLevel:string|null, matchedName:string|null}|null>}
 */
export async function resolveAddress (address) {
  const addr = String(address || '').trim()
  if (!addr) return null
  const hit = geoMemo.get(addr)
  if (hit) {
    if (hit.ok) return hit.result
    if (Date.now() - hit.ts < GEO_FAIL_TTL) return null
    geoMemo.delete(addr) // 失败短缓存过期 → 重试
  }
  const r = await geocodeOnce(addr)
  geoMemo.set(addr, r ? { ok: true, result: r } : { ok: false, ts: Date.now() })
  return r
}

// ---------- 渲染分发：坐标唯一入口，provider 无能即 reject ----------
/**
 * 创建地图视图
 * @param {HTMLElement} el
 * @param {Object} p { latitude, longitude, coordinateSystem?, zoom?, interactive?, minZoom?, maxZoom? }
 *   interactive:false = preview-only：provider 关闭全部地图手势（拖拽/双击/滚轮/捏合/键盘），
 *   页面滚动不被地图劫持；默认 true（标准交互地图——2026-09-19 订单地图恢复交互：
 *   pinch/滚轮/+/- /拖动全开，缩放范围由 minZoom/maxZoom 约束不锁死）
 * @returns {Promise<{destroy:Function, getZoom:Function, getCenter:Function}>}
 */
export async function createMap (el, { latitude, longitude, coordinateSystem = LEGACY_COORDINATE_SYSTEM, zoom = 15, interactive = true, minZoom, maxZoom } = {}) {
  const cfg = await getMapConfig()
  if (!cfg.enabled) throw new Error('map_unavailable')
  return PROVIDERS[cfg.provider].createMap(el, {
    key: cfg.key, // tianditu（浏览器端 tk）
    jsKey: cfg.jsKey, // amap（迁移兼容）
    serviceHost: cfg.serviceHost, // amap（迁移兼容）
    latitude,
    longitude,
    coordinateSystem,
    zoom,
    interactive,
    minZoom,
    maxZoom
  })
}
